import { LOCALE_INFO, SOURCE_LOCALE, loadMessages, type Locale, type Messages } from "@omony/i18n";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { I18nProvider } from "react-aria-components";
import { IntlProvider } from "react-intl";
import { applyTheme, initialLocale, initialTheme, storeLocale, storeTheme, type ThemePref } from "./prefs.js";

interface UiPrefs {
  locale: Locale;
  setLocale: (l: Locale) => void;
  theme: ThemePref;
  setTheme: (t: ThemePref) => void;
}

const PrefsContext = createContext<UiPrefs | null>(null);

export function useUiPrefs(): UiPrefs {
  const c = useContext(PrefsContext);
  if (!c) throw new Error("useUiPrefs außerhalb von AppRoot");
  return c;
}

/**
 * Oberflächensprache (Personal) mit Richtung, Theme und i18n-Providern.
 * Die Patientensprache bekommt später einen eigenen, verschachtelten Provider (ARCHITEKTUR §10.4).
 */
export function AppRoot({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [theme, setThemeState] = useState<ThemePref>(initialTheme);
  const [messages, setMessages] = useState<Messages | null>(null);

  useEffect(() => {
    let alive = true;
    void loadMessages(locale).then((m) => alive && setMessages(m));
    const root = document.documentElement;
    root.lang = locale;
    root.dir = LOCALE_INFO[locale].dir;
    return () => {
      alive = false;
    };
  }, [locale]);

  useEffect(() => applyTheme(theme), [theme]);

  const value = useMemo<UiPrefs>(
    () => ({
      locale,
      theme,
      setLocale: (l) => {
        storeLocale(l);
        setLocaleState(l);
      },
      setTheme: (t) => {
        storeTheme(t);
        setThemeState(t);
      },
    }),
    [locale, theme],
  );

  if (!messages) return null;
  return (
    <PrefsContext.Provider value={value}>
      <IntlProvider
        locale={locale}
        defaultLocale={SOURCE_LOCALE}
        messages={messages}
        onError={(e) => {
          if (import.meta.env.DEV) console.error(e);
        }}
      >
        <I18nProvider locale={locale}>{children}</I18nProvider>
      </IntlProvider>
    </PrefsContext.Provider>
  );
}
